import ChevronDown from "@shared/svg/chevron-down.svg?react";
import MoreIcon from "@shared/svg/more.svg?react";
import ProductsIcon from "@shared/svg/products.svg?react";
import QrCodeIcon from "@shared/svg/qr-code.svg?react";
import BodyText, { BODY_TEXT_SIZES } from "@shared/ui/BodyText";
import { BulkSelectCheckbox } from "@shared/ui/Inputs";
import Headline, { HEADLINE_SIZES } from "@shared/ui/Headline";
import { MakulaIcon } from "@shared/ui/MakulaIcon";
import Tag, { TAG_COLORS } from "@shared/ui/Tag";
import Tooltip from "@shared/ui/Tooltip";
import { getMachineThumbnail } from "@shared/utils/getMachineThumbnail";
import React, { useState } from "react";
import { useIntl } from "react-intl";

import { AssetThumbnail, type ThumbnailSize } from "./AssetThumbnail";

// Container size classes mirroring AssetThumbnail so the in-slot checkbox keeps
// the exact same footprint as the thumbnail it replaces (no layout shift).
const THUMBNAIL_CONTAINER_CLASS: Record<ThumbnailSize, string> = {
  sm: "w-xl h-xl",
  md: "w-2xl h-2xl",
  lg: "w-3xl h-3xl",
  xl: "w-4xl h-4xl",
  "2xl": "w-5xl h-5xl",
  "3xl": "w-6xl h-6xl",
  "4xl": "w-8xl h-8xl",
};

export interface AssetCardData {
  id: string;
  name: string;
  serialNumber?: string | null;
  thumbnail?: string | null;
  image?: string | null;
  totalChildrenCount: number;
  isQRCodeEnabled?: boolean;
  teams?: { id: string; name: string; teamColor?: string }[];
  template?: { id: string; title: string } | null;
  hierarchy: { id: string; name: string }[];
  connectionName?: string | null;
  oem?: { logo?: string | null } | null;
}

interface AssetCardProps {
  asset: AssetCardData;
  /** Show expand/collapse chevron */
  showExpandIcon?: boolean;
  /** Whether the card is in expanded state (for chevron rotation) */
  isExpanded?: boolean;
  /** Callback when expand/collapse is toggled */
  onToggleExpand?: () => void;
  /** Whether search mode is active (shows breadcrumb, hides expand) */
  isSearching?: boolean;
  /** Show company name in search results */
  showCompany?: boolean;
  /** Show QR code button */
  showQrCode?: boolean;
  /** Callback when card is clicked */
  onClick?: () => void;
  /** Callback when QR code button is clicked */
  onQrCodeClick?: () => void;
  /** Callback when more button is clicked */
  onMoreClick?: () => void;
  /** Additional className for the card container */
  className?: string;
  /** Custom style for the card container */
  style?: React.CSSProperties;

  showTeam?: boolean;
  /** Max number of team tags to show before "+N" */
  maxVisibleTeams?: number;
  /** Show connection/company name next to serial number */
  showConnection?: boolean;

  trailingComponent?: React.ReactNode;
  /** Extra tag(s) shown before the QR code button, e.g. a status tag */
  badge?: React.ReactNode;
  /** Size of the asset thumbnail image */
  thumbnailSize?: ThumbnailSize;
  /** Show the product/template badge on the thumbnail */
  showTemplateBadge?: boolean;
  /**
   * When true, the leading thumbnail is replaced by a selection checkbox on
   * hover (and permanently once selected or any item is selected). Opt-in so
   * non-selection usages keep their thumbnail.
   */
  selectable?: boolean;
  /** Whether this card is currently selected (bulk selection mode). */
  isSelected?: boolean;
  /** Force the checkbox to stay visible (set once ≥1 item is selected). */
  anySelected?: boolean;
  /** Toggle this card's selection. */
  onToggleSelect?: () => void;
}

/**
 * AssetCard (V2)
 *
 * A reusable asset card component matching the Figma design.
 * Displays asset thumbnail, name, serial number, metadata, and action buttons.
 * Can be used standalone or within a tree structure.
 */
export const AssetCard: React.FC<AssetCardProps> = ({
  asset,
  showExpandIcon = false,
  isExpanded = false,
  onToggleExpand,
  isSearching = false,
  showCompany = true,
  showQrCode = true,
  onClick,
  onQrCodeClick,
  onMoreClick,
  className = "",
  style,
  showTeam = true,
  maxVisibleTeams = 2,
  showConnection = false,
  trailingComponent = null,
  badge = null,
  thumbnailSize = "xl",
  showTemplateBadge = true,
  selectable = false,
  isSelected = false,
  anySelected = false,
  onToggleSelect,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const { messages } = useIntl();

  const thumbnailSizeClass = THUMBNAIL_CONTAINER_CLASS[thumbnailSize];

  // Get card translations
  const cardMessages = (
    messages?.["sharedAssets"] as unknown as Record<string, unknown>
  )?.["card"] as Record<string, string> | undefined;

  // Build breadcrumb from hierarchy
  const breadcrumb =
    asset.hierarchy?.length > 0
      ? asset.hierarchy.map((h) => h.name).join(" > ")
      : null;

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleExpand?.();
  };

  const handleQrCodeClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onQrCodeClick?.();
  };

  const handleMoreClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onMoreClick?.();
  };

  const handleClick = () => {
    onClick?.();
  };

  return (
    <div
      className={`
        flex flex-col sm:flex-row items-start sm:items-center
        gap-xs sm:gap-lg p-sm sm:p-lg rounded-lg relative
        border border-solid transition-colors cursor-pointer
        ${
          isHovered || isSelected
            ? "bg-accent border-primary"
            : "bg-primary border-primary"
        }
        ${className}
      `}
      style={style}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleClick}
    >
      {/* Row 1 (mobile) / Left Section (desktop): Chevron + Thumbnail + Info */}
      <div className="flex items-center gap-xs sm:gap-sm flex-1 min-w-0 w-full sm:w-auto pr-3xl sm:pr-0">
        {/* Expand/Collapse Chevron */}
        {showExpandIcon && (
          <button
            className="flex-shrink-0 w-xl h-xl sm:w-2xl sm:h-2xl pt-px pb-px pl-[6px] pr-2xs flex items-center justify-center bg-transparent border-none cursor-pointer"
            onClick={handleToggle}
            aria-label={isExpanded ? "Close" : "Open"}
          >
            <MakulaIcon
              icon={<ChevronDown />}
              className={`w-lg h-lg transition-transform duration-200 ${
                isExpanded ? "" : "-rotate-90"
              }`}
              color="text-tertiary"
            />
          </button>
        )}

        {/* Content wrapper */}
        <div className="flex items-center gap-sm sm:gap-lg flex-1 min-w-0">
          {/* Asset Thumbnail with Product Badge — swapped for a selection
              checkbox (same thumbnail footprint) on hover or once selected,
              so the title never shifts. */}
          {selectable && (isHovered || isSelected || anySelected) ? (
            <BulkSelectCheckbox
              checked={isSelected}
              onToggle={() => onToggleSelect?.()}
              className={thumbnailSizeClass}
            />
          ) : (
            <div className="relative flex-shrink-0">
              <AssetThumbnail
                thumbnail={getMachineThumbnail(asset as any)}
                alt={asset.name}
                size={thumbnailSize}
              />
              {showTemplateBadge && asset.template && (
                <div
                  className="absolute -bottom-2xs -right-2xs w-lg h-lg bg-secondary rounded flex items-center justify-center text-white"
                  data-tip={asset.template.title}
                  data-for={`template-tooltip-${asset.id}`}
                >
                  <MakulaIcon icon={<ProductsIcon />} className="w-md h-md" />
                  <Tooltip id={`template-tooltip-${asset.id}`} place="top" />
                </div>
              )}
            </div>
          )}

          {/* Asset Info */}
          <div className="flex flex-col flex-1 min-w-0">
            <Headline size={HEADLINE_SIZES.X_SMALL} className="truncate">
              {asset.name}
            </Headline>
            <div className="flex items-center gap-xs sm:gap-sm flex-wrap">
              {/* Serial Number */}
              {asset.serialNumber && (
                <BodyText
                  size={BODY_TEXT_SIZES.X_SMALL}
                  color="text-secondary"
                  className="truncate"
                >
                  {asset.serialNumber}
                </BodyText>
              )}

              {/* Company/Connection Name */}
              {((isSearching && showCompany) || showConnection) &&
                asset.connectionName && (
                  <>
                    <span className="text-tertiary text-xs">•</span>
                    <BodyText
                      size={BODY_TEXT_SIZES.X_SMALL}
                      color="text-secondary"
                      className="truncate"
                    >
                      {asset.connectionName}
                    </BodyText>
                  </>
                )}

              {/* Total Children Count */}
              {asset.totalChildrenCount > 0 && (
                <>
                  <span className="text-tertiary text-xs">•</span>
                  <BodyText
                    size={BODY_TEXT_SIZES.X_SMALL}
                    color="text-secondary"
                    className="whitespace-nowrap"
                  >
                    {asset.totalChildrenCount}{" "}
                    {asset.totalChildrenCount > 1
                      ? cardMessages?.["subAssets"] || "Sub Assets"
                      : cardMessages?.["subAsset"] || "Sub Asset"}
                  </BodyText>
                </>
              )}

              {/* Hierarchy breadcrumb when searching */}
              {isSearching && breadcrumb && (
                <>
                  <span className="text-tertiary text-xs">•</span>
                  <BodyText
                    size={BODY_TEXT_SIZES.X_SMALL}
                    color="text-tertiary"
                    className="truncate"
                  >
                    {breadcrumb}
                  </BodyText>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Row 2 (mobile) / Right Section (desktop): Teams + QR + More */}
      <div className="flex items-center gap-xs sm:gap-md flex-shrink-0 w-full sm:w-auto ml-[44px] sm:ml-0">
        {/* Team Tags - mobile: 1 tag, desktop: maxVisibleTeams */}
        {showTeam && asset.teams && asset.teams.length > 0 && (
          <>
            {/* Mobile: show 1 team + overflow */}
            <div className="flex sm:hidden items-center gap-xs">
              <Tag
                key={asset.teams[0].id}
                label={asset.teams[0].name}
                style={
                  asset.teams[0].teamColor
                    ? {
                        color: asset.teams[0].teamColor,
                        background: `${asset.teams[0].teamColor}1A`,
                      }
                    : undefined
                }
                color={!asset.teams[0].teamColor ? TAG_COLORS.RED : undefined}
              />
              {asset.teams.length > 1 && (
                <Tag
                  label={`+${asset.teams.length - 1}`}
                  color={TAG_COLORS.BLUE}
                />
              )}
            </div>
            {/* Desktop: show maxVisibleTeams */}
            <div className="hidden sm:flex items-center gap-xs">
              {asset.teams.slice(0, maxVisibleTeams).map((team) => (
                <Tag
                  key={team.id}
                  label={team.name}
                  style={
                    team.teamColor
                      ? {
                          color: team.teamColor,
                          background: `${team.teamColor}1A`,
                        }
                      : undefined
                  }
                  color={!team.teamColor ? TAG_COLORS.RED : undefined}
                />
              ))}
              {asset.teams.length > maxVisibleTeams && (
                <Tag
                  label={`+${asset.teams.length - maxVisibleTeams}`}
                  color={TAG_COLORS.BLUE}
                />
              )}
            </div>
          </>
        )}

        {badge}

        {/* QR Code Button */}
        {showQrCode && asset.isQRCodeEnabled && (
          <button
            className="flex-shrink-0 w-2xl h-2xl sm:w-3xl sm:h-3xl flex items-center justify-center border border-solid border-primary rounded-lg bg-primary hover:bg-accent transition-colors"
            onClick={handleQrCodeClick}
            aria-label={cardMessages?.["showQrCode"] || "Show QR Code"}
          >
            <MakulaIcon
              icon={<QrCodeIcon />}
              className="w-md h-md sm:w-xl sm:h-xl"
            />
          </button>
        )}

        {/* More Options Button - absolute on mobile, inline on desktop */}
        {onMoreClick && (
          <button
            className="absolute top-sm right-sm sm:static flex-shrink-0 w-3xl h-3xl flex items-center justify-center bg-transparent border-none cursor-pointer"
            onClick={handleMoreClick}
            aria-label={cardMessages?.["moreOptions"] || "More options"}
          >
            <MakulaIcon icon={<MoreIcon />} className="w-xl h-xl" />
          </button>
        )}

        {trailingComponent && (
          <div
            className="absolute top-sm right-sm sm:static"
            onClick={(e) => e.stopPropagation()}
          >
            {trailingComponent}
          </div>
        )}
      </div>
    </div>
  );
};
